// Date display + parse helpers. Adopts MM/DD/YYYY as the canonical
// display format for the dashboard (matches ECU_Database convention).
// Storage everywhere is BIGINT epoch ms - per tech-stack-conventions.md
// section 2 - so these helpers take/emit epoch ms only.
//
// For PDF/email contexts where a longer form reads better, use
// fmtDateLong instead - same input, different rendering.

export const MS_PER_DAY = 86_400_000;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * "05/29/2026" - canonical short format used everywhere in the dashboard.
 * Falls back to em dash for null/undefined/non-finite inputs so callers
 * can pass DB nulls without an extra guard.
 */
export function fmtDate(epochMs: number | null | undefined): string {
  if (epochMs === null || epochMs === undefined || !Number.isFinite(epochMs)) return '—';
  const d = new Date(epochMs);
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;
}

/**
 * "May 29, 2026" - longer form for headers, PDFs, emails where
 * MM/DD/YYYY would feel terse.
 */
export function fmtDateLong(epochMs: number | null | undefined): string {
  if (epochMs === null || epochMs === undefined || !Number.isFinite(epochMs)) return '—';
  return new Date(epochMs).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * "05/29/2026 2:30 PM" - short date plus 12-hour time. Used for
 * audit logs, evidence intake timestamps, calendar entries, time entries.
 *
 * No comma between the two: the date is already delimited by its slashes, so
 * the comma was pure noise in a table cell.
 */
export function fmtDateTime(epochMs: number | null | undefined): string {
  if (epochMs === null || epochMs === undefined || !Number.isFinite(epochMs)) return '—';
  const d = new Date(epochMs);
  const datePart = `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;
  const timePart = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart} ${timePart}`;
}

/** "02:30 PM" — time of day alone, for an end that shares its start's date. */
export function fmtTime(epochMs: number | null | undefined): string {
  if (epochMs === null || epochMs === undefined || !Number.isFinite(epochMs)) return '—';
  return new Date(epochMs).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** True when two instants fall on the same LOCAL calendar day. Compares the
 *  rendered date parts rather than day-bucketing the epochs, so DST shifts and
 *  any timezone offset can't split a day in two. */
export function isSameLocalDay(
  a: number | null | undefined,
  b: number | null | undefined,
): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  const x = new Date(a);
  const y = new Date(b);
  return x.getFullYear() === y.getFullYear()
    && x.getMonth() === y.getMonth()
    && x.getDate() === y.getDate();
}

/**
 * The date and the time as separate pieces, for a table cell that stacks them
 * on two lines. `time` is null when the value sits on local midnight — the
 * same "no real time of day was recorded" test `fmtDateMaybeTime` applies, so
 * the two can't drift.
 */
export function dateTimeParts(
  epochMs: number | null | undefined,
): { date: string; time: string | null } {
  if (epochMs === null || epochMs === undefined || !Number.isFinite(epochMs)) {
    return { date: '—', time: null };
  }
  const d = new Date(epochMs);
  const isMidnight = d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
  return { date: fmtDate(epochMs), time: isMidnight ? null : fmtTime(epochMs) };
}

/**
 * The end of a span as pieces, next to its start. Null when there is no end.
 * `date` is null when the two share a calendar day — the start's date is right
 * there, and repeating it on every row says nothing. A date that DOES appear
 * therefore means something: this entry ran past midnight.
 */
export function rangeEndParts(
  startMs: number | null | undefined,
  endMs: number | null | undefined,
): { date: string | null; time: string } | null {
  if (endMs === null || endMs === undefined || !Number.isFinite(endMs)) return null;
  return {
    date: isSameLocalDay(startMs, endMs) ? null : fmtDate(endMs),
    time: fmtTime(endMs),
  };
}

/**
 * "05/29/2026" for calendar-day entries, "05/29/2026, 02:30 PM" when the
 * value carries a real time-of-day. Time entries store their start moment
 * in `date` (like the timer does), but legacy manual rows were pinned to
 * local midnight — so show the time only when it's non-midnight.
 */
export function fmtDateMaybeTime(epochMs: number | null | undefined): string {
  if (epochMs === null || epochMs === undefined || !Number.isFinite(epochMs)) return '—';
  const d = new Date(epochMs);
  const isMidnight =
    d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
  return isMidnight ? fmtDate(epochMs) : fmtDateTime(epochMs);
}

/**
 * Convert a native HTML date-input string ("2026-05-29") to an epoch
 * ms value pinned to local midnight. Returns null for blank input so
 * callers can chain with optional state.
 */
export function dateInputToEpoch(ymd: string | null | undefined): number | null {
  if (!ymd) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const [, y, mo, d] = m;
  // Local midnight - matches the "calendar-day" expectation of a date
  // input where time-of-day is irrelevant.
  const date = new Date(Number(y), Number(mo) - 1, Number(d), 0, 0, 0, 0);
  return date.getTime();
}

/**
 * Convert an epoch ms value to "YYYY-MM-DD" suitable for an
 * <input type="date"> value attribute or flatpickr's defaultDate.
 * Uses LOCAL date so what the user sees on the picker matches the
 * displayed string.
 */
export function epochToDateInput(epochMs: number | null | undefined): string {
  if (epochMs === null || epochMs === undefined || !Number.isFinite(epochMs)) return '';
  const d = new Date(epochMs);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Convenience: number of days between two epoch ms values (positive if b > a). */
export function daysBetween(aEpochMs: number, bEpochMs: number): number {
  return Math.round((bEpochMs - aEpochMs) / MS_PER_DAY);
}

/**
 * Local midnight on the day containing `epochMs` — the start of that calendar
 * day.
 *
 * Built from the rendered Y/M/D rather than by flooring the epoch, so a
 * non-whole-hour UTC offset or a DST boundary can't land it on the wrong day.
 */
export function startOfDay(epochMs: number): number {
  const d = new Date(epochMs);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
}

/** Local 23:59:59.999 on the day containing `epochMs` — the last instant of
 *  that calendar day, so a range ending here covers the whole day. */
export function endOfDay(epochMs: number): number {
  const d = new Date(epochMs);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
}
